import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import type { IServiceSelect } from 'widgets/project-creation-stepper/model';
export interface TypeSelect {
  name: string;
  label: string;
}
interface IServiceProvider {
  firstLoad: boolean;
  setFirstLoad: Dispatch<SetStateAction<boolean>>;
  serviceType: IServiceSelect['name'] | undefined; // Allow undefined to match state initialization
  setServiceType: Dispatch<SetStateAction<IServiceSelect['name'] | undefined>>; // Ensure the setter is correctly typed
  selectedProjectType: string | undefined;
  setSelectedProjectType: Dispatch<SetStateAction<string | undefined>>;
  projectTypes: TypeSelect[];
  setProjectTypes: Dispatch<SetStateAction<TypeSelect[]>>;
  uploadType: 'interface' | 'github';
  setUploadType: Dispatch<SetStateAction<'interface' | 'github'>>;
  projectSlug: string;
  setProjectSlug: Dispatch<SetStateAction<string>>;
  selectedMarketplace: string | undefined;
  setSelectedMarketplace: Dispatch<SetStateAction<string | undefined>>;
}
const ServiceContext = createContext<IServiceProvider>(undefined);
export const useServiceType = () => useContext(ServiceContext);
export function ServiceProvider({
  children,
  initialState
}: {
  children: ReactNode;
  initialState: Partial<IServiceProvider>;
}) {
  const [firstLoad, setFirstLoad] = useState(true);
  const [serviceType, setServiceType] = useState<IServiceSelect['name'] | undefined>(initialState.serviceType);
  const [selectedProjectType, setSelectedProjectType] = useState<string | undefined>(initialState.selectedProjectType);
  const [projectTypes, setProjectTypes] = useState<TypeSelect[]>(initialState.projectTypes ?? []);
  const [uploadType, setUploadType] = useState<'interface' | 'github'>('github');
  const [projectSlug, setProjectSlug] = useState<string>('');
  const [selectedMarketplace, setSelectedMarketplace] = useState<string | undefined>(initialState.selectedMarketplace);
  const contextValue = useMemo(() => ({
    firstLoad,
    setFirstLoad,
    serviceType,
    setServiceType,
    selectedProjectType,
    setSelectedProjectType,
    projectTypes,
    setProjectTypes,
    uploadType,
    setUploadType,
    projectSlug,
    setProjectSlug,
    selectedMarketplace,
    setSelectedMarketplace
  }), [firstLoad, projectSlug, projectTypes, selectedMarketplace, selectedProjectType, serviceType, uploadType]);
  return <ServiceContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="ServiceProvider" data-sentry-source-file="service-provider.tsx">
      {children}
    </ServiceContext.Provider>;
}